import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import {
  ApplicationConfig,
  CSP_NONCE,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { cryptoActionIgnoreList } from '@zero/crypto/domain';
import { CLEVER_PUSH_ENVIRONMENT_CONFIG, CleverPushEnvironmentConfig, CleverPushService } from '@zero/feature-clever-push/base';
import { CleverPushWebService } from '@zero/feature-clever-push/web';
import { homeActionsIgnoreList } from '@zero/home/domain';
import {
  ContractConfirmationServiceBase,
  contractConfirmationInterceptor,
  provideContractConfirmationFeature,
} from '@zero/shared/feature-contract-confirmation/base';
import { ContractConfirmationService } from '@zero/shared/feature-contract-confirmation/web';
import { provideDividendReInvestFeature } from '@zero/shared/feature-dividend-re-invest/base';
import {
  AbstractAuthService,
  AbstractDataService,
  AbstractDiffusionSessionService,
  AbstractMetaDataService,
  AbstractTradingService,
  AbstractUserMessagesHolderService,
} from '@zero/shared/util-abstract-services';
import {
  AbstractAlertService,
  clearStateMetaReducer,
  maintenanceWorkInterceptor,
  positionsActions,
  provideGlobalSate,
  watchlistActions,
} from '@zero/shared/util-global-state';
import { TranslocoHttpLoader, WafTokenInterceptor, provideSentry } from '@zero/shared/util-services';
import {
  AmplifyAuthService,
  AuthInterceptor,
  AuthService,
  ClientInfoInterceptor,
  DataService,
  DiffusionSessionService,
  MetaDataService,
  NgbDateDEAdapter,
  NgbDateDEParserFormatter,
  ScriptResponse,
  ScriptService,
  TradingService,
  UserMessagesHolderService,
} from '@zero/shared/util-web-app-services';
import { BASE_PATH, Configuration, ConfigurationParameters, ZeroApiModule } from '@zero/shared/util-zero-api-client';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { getTimezoneOffset } from 'date-fns-tz';
import { environment } from 'environments/environment';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideCacheableAnimationLoader, provideLottieOptions } from 'ngx-lottie';
import { APP_ROUTES } from './app.routes';

registerLocaleData(localeDe);

function initializeWaf(scriptService: ScriptService) {
  return (): Promise<ScriptResponse | void> => {
    if (!environment.awsWafChallengePlugin) {
      console.log('WAF plugin is not defined and cannot be loaded!');
      return Promise.resolve();
    }

    if (!environment.production) {
      console.log('WAF plugin is not supported on localhost!');
      return Promise.resolve();
    }

    return scriptService.loadScript(environment.awsWafChallengePlugin).catch((e) => {
      console.error('WAF plugin failed to load:', e);
      Promise.resolve();
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initializeWaf(inject(ScriptService));
      return initializerFn();
    }),
    provideRouter(
      APP_ROUTES,

      withPreloading(PreloadAllModules),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      }),
      withEnabledBlockingInitialNavigation(),
    ),
    provideStore(
      {},
      {
        metaReducers: [clearStateMetaReducer],
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      },
    ),
    provideEffects(),
    provideHttpClient(withInterceptors([contractConfirmationInterceptor, maintenanceWorkInterceptor]), withInterceptorsFromDi()),
    provideAnimations(),
    provideLottieOptions({
      player: () => import(/* webpackChunkName: 'lottie-web' */ 'lottie-web'),
    }),
    provideCharts(withDefaultRegisterables()),
    provideContractConfirmationFeature(),
    provideDividendReInvestFeature(),
    provideCacheableAnimationLoader(),
    provideGlobalSate(),
    provideTransloco({
      config: {
        availableLangs: ['de'],
        defaultLang: 'de',
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    { provide: AbstractAlertService, useExisting: UserMessagesHolderService },
    { provide: AbstractDiffusionSessionService, useExisting: DiffusionSessionService },
    { provide: AbstractMetaDataService, useExisting: MetaDataService },
    { provide: AuthService, useClass: AmplifyAuthService },
    { provide: AbstractDataService, useExisting: DataService },
    { provide: AbstractTradingService, useExisting: TradingService },
    { provide: AbstractUserMessagesHolderService, useExisting: UserMessagesHolderService },
    { provide: NgbDateParserFormatter, useClass: NgbDateDEParserFormatter },
    { provide: NgbDateAdapter, useClass: NgbDateDEAdapter },
    { provide: ContractConfirmationServiceBase, useClass: ContractConfirmationService },
    { provide: AbstractAuthService, useExisting: AuthService },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ClientInfoInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WafTokenInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: `+0${getTimezoneOffset('Europe/Berlin') / 60 / 60 / 1000}00` } },
    {
      provide: CSP_NONCE,
      useValue: 'XbBjCIP5DwHM7trsM6KVLyGUcdCxmWSk8rOJRcSCDmg',
    },
    { provide: CleverPushService, useClass: CleverPushWebService },
    {
      provide: CLEVER_PUSH_ENVIRONMENT_CONFIG,
      useValue: {
        ...environment.cleverPush,
        platform: 'WEB',
        notificationServiceUrl: environment.notificationServiceUrl,
        priceAlertServiceUrl: '/api/pricealert',
      } satisfies CleverPushEnvironmentConfig,
    },
    provideSentry(),
    isDevMode()
      ? provideStoreDevtools({
          maxAge: 25,
          autoPause: true,
          actionsBlocklist: [
            positionsActions.calculatePortfolioPerformance.type,
            positionsActions.positionsPerformanceCalculated.type,
            watchlistActions.watchlistPerformanceCalculated.type,
            ...cryptoActionIgnoreList,
            ...homeActionsIgnoreList,
          ],
        })
      : [],
    importProvidersFrom([
      NgxsRouterPluginModule.forRoot(),
      GoogleTagManagerModule.forRoot({
        id: environment.gtm.id,
        gtm_resource_path: `${environment.gtm.transportUrl}/tradeclient.js`,
      }),
    ]),
    { provide: BASE_PATH, useValue: environment.apihost },
    importProvidersFrom([ZeroApiModule.forRoot(apiConfigFactory)]),
  ],
};

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: environment.apihost,
    withCredentials: environment.sendAuthHeaderForApi,
    credentials: {
      BasicAuth: environment.apihost_ba.slice(6),
      //'Cognito-ID-Token' solved over AuthInterceptor
    },
  };
  return new Configuration(params);
}
